import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Select, Tag, Card, Row, Col, Statistic, Divider } from 'antd';
import { 
  SearchOutlined, 
  FireOutlined, 
  CalendarOutlined, 
  EyeOutlined,
  MessageOutlined,
  LikeOutlined,
  EditOutlined,
  UserOutlined,
  BookOutlined,
  CoffeeOutlined,
  CodeOutlined
} from '@ant-design/icons';
import PostCard from '../../components/PostCard';
import dayjs from 'dayjs';
import './index.css';

const { Search } = Input;
const { Option } = Select;

const Home = ({ posts = [] }) => {
  const [filteredPosts, setFilteredPosts] = useState(posts);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    categories: 0,
    today: 0,
    views: 0
  });

  // 计算统计数据
  useEffect(() => {
    const categories = new Set(posts.map(p => p.category));
    const today = dayjs().format('YYYY-MM-DD');
    const todayPosts = posts.filter(p => p.date === today);
    
    // 按浏览量排序获取特色文章
    const sortedByViews = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 2);
    const recent = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);
    
    setFeaturedPosts(sortedByViews);
    setRecentPosts(recent);
    
    setStats({
      total: posts.length,
      categories: categories.size,
      today: todayPosts.length,
      views: posts.reduce((sum, post) => sum + (post.views || 0), 0)
    });
  }, [posts]);

  // 过滤文章
  useEffect(() => {
    let result = [...posts];
    
    if (searchQuery) {
      result = result.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (selectedCategory !== '全部') {
      result = result.filter(post => post.category === selectedCategory);
    }
    
    setFilteredPosts(result);
  }, [posts, searchQuery, selectedCategory]);

  // 获取分类
  const categories = ['全部', ...new Set(posts.map(p => p.category))];
  
  // 热门标签
  const popularTags = posts
    .flatMap(p => p.tags || [])
    .reduce((acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {});
  
  const sortedTags = Object.entries(popularTags)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10);

  // 获取热门作者
  const popularAuthors = posts
    .reduce((acc, post) => {
      acc[post.author] = (acc[post.author] || 0) + 1;
      return acc;
    }, {});

  const topAuthors = Object.entries(popularAuthors)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  return (
    <div className="home-page">
      {/* 顶部搜索栏 */}
      <div className="home-hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1>欢迎来到我的博客</h1>
            <p className="hero-subtitle">记录生活点滴，分享技术思考</p>
            
            <div className="hero-search">
              <Search
                placeholder="搜索文章标题、内容或标签..."
                prefix={<SearchOutlined />}
                size="large"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                enterButton="搜索"
                className="search-input"
              />
              
              <div className="quick-categories">
                {categories.filter(cat => cat !== '全部').slice(0, 5).map(cat => (
                  <Tag 
                    key={cat} 
                    className={`quick-category ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
          
          <div className="hero-stats">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <div className="stat-card">
                  <Statistic
                    title="文章总数"
                    value={stats.total}
                    prefix={<BookOutlined />}
                    className="home-statistic"
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-card">
                  <Statistic
                    title="分类数量"
                    value={stats.categories}
                    prefix={<CodeOutlined />}
                    className="home-statistic"
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-card">
                  <Statistic
                    title="今日更新"
                    value={stats.today}
                    prefix={<CalendarOutlined />}
                    className="home-statistic"
                  />
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-card">
                  <Statistic
                    title="总阅读量"
                    value={stats.views}
                    prefix={<EyeOutlined />}
                    className="home-statistic"
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* 主要内容区 */}
      <div className="home-main">
        <div className="main-container">
          {/* 左侧：文章列表 */}
          <div className="posts-section">
            {/* 特色文章 */}
            {featuredPosts.length > 0 && (
              <div className="featured-section">
                <div className="section-header">
                  <h2><FireOutlined /> 热门推荐</h2>
                </div>
                
                <div className="featured-grid">
                  {featuredPosts.map(post => (
                    <div key={post.id} className="featured-post">
                      <div className="featured-image">
                        <img src={post.image || `https://picsum.photos/600/400?random=${post.id}`} alt={post.title} />
                        <Tag className="featured-category" color="gold">{post.category}</Tag>
                      </div>
                      <div className="featured-content">
                        <h3 className="featured-title">
                          <Link to={`/post/${post.id}`}>{post.title}</Link>
                        </h3>
                        <p className="featured-excerpt">
  {post.excerpt || (post.content && post.content.substring(0, 100)) || '暂无摘要'}...
</p>

{post.excerpt || (post.content && post.content.substring(0, 80)) || '暂无摘要'}...

                        <div className="featured-meta">
                          <span><UserOutlined /> {post.author}</span>
                          <span><CalendarOutlined /> {post.date}</span>
                          <span><EyeOutlined /> {post.views || 0}</span>
                          <span><LikeOutlined /> {post.likes || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Divider />

            {/* 最新文章 */}
            <div className="recent-section">
              <div className="section-header">
                <div className="section-title-group">
                  <h2>📝 最新文章</h2>
                  <Select
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    size="middle"
                    className="category-select"
                    style={{ width: 120 }}
                  >
                    {categories.map(cat => (
                      <Option key={cat} value={cat}>{cat}</Option>
                    ))}
                  </Select>
                </div>
                <Link to="/archive" className="view-all-link">
                  查看全部 <span>→</span>
                </Link>
              </div>

              {filteredPosts.length > 0 ? (
                <div className="posts-grid">
                  {filteredPosts.slice(0, 6).map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              ) : (
                <div className="empty-posts">
                  <div className="empty-content">
                    <CoffeeOutlined className="empty-icon" />
                    <p>暂时没有相关文章</p>
                    <div className="empty-actions">
                      <Button onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('全部');
                      }}>
                        查看全部文章
                      </Button>
                      <Link to="/write">
                        <Button type="primary" icon={<EditOutlined />}>
                          去写一篇
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 右侧：侧边栏 */}
          <div className="sidebar">
            {/* 热门标签 */}
            <Card title="🔥 热门标签" className="sidebar-card">
              <div className="tags-cloud">
                {sortedTags.map(([tag, count]) => (
                  <Tag 
                    key={tag}
                    className="tag-item"
                    onClick={() => setSearchQuery(tag)}
                    style={{ 
                      fontSize: `${Math.min(14 + count, 18)}px`,
                      opacity: count > 5 ? 1 : 0.7
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </Card>

            {/* 热门作者 */}
            <Card title="👥 热门作者" className="sidebar-card">
              <div className="authors-list">
                {topAuthors.map(([author, count]) => (
                  <div key={author} className="author-item">
                    <div className="author-info">
                      {/* <Avatar 
                        size={32}
                        style={{ backgroundColor: '#1890ff' }}
                        icon={<UserOutlined />}
                      /> */}
                      <div className="author-details">
                        <span className="author-name">{author}</span>
                        <span className="author-count">{count} 篇文章</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* 分类统计 */}
            <Card title="📂 文章分类" className="sidebar-card">
              <div className="categories-list">
                {categories.filter(cat => cat !== '全部').map(cat => {
                  const count = posts.filter(p => p.category === cat).length;
                  return (
                    <div 
                      key={cat} 
                      className={`category-item ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      <span className="category-name">{cat}</span>
                      <span className="category-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* 写作引导 */}
            <Card className="write-guide-card">
              <div className="write-guide-content">
                <EditOutlined className="guide-icon" />
                <h3>开始创作</h3>
                <p>分享你的知识和经验</p>
                <Link to="/write">
                  <Button type="primary" size="large" block>
                    写文章
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;